import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { ArtifactsTypeForBatchRequest, IRequestArtifactsPayload, IRequestMeetingArtifactDTO, RecordingType, RequestArtifactsTab, ResolutionMp4, TGetArtifactsSourcesRequestParams, TRequestArtifactsTab } from '../../../../../api/artifacts.dao';
import { IEventMeetingsDTO, IMeetingsDAO } from '../../../../../api/meetings.dao';
import { MAX_QUANTITY_OF_SELECTED_MEETINGS } from '../../../../../constants/artifacts.constants';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { SortOrder } from '../../../../../api/shared.dto';
import { ConnectionType } from '../../../../../api/connections.dao';
import { getShortRichTextLengthError } from '../../../../../infrastructure/utils/form-validation.utils';
import { transpileClassesToInlineStyles } from '../../../../global/rich-text-editor/utils/transpiler';

interface IEventSettings {
    timeZone: string;
    eventId: number;
    accountId: number;
}

class RequestArtifactsPopoverViewModel {
    private isMeetingsAlreadyFetched: Record<TRequestArtifactsTab, boolean> = {
        'artifacts': false,
        'replays': false,
    };

    @mobx.observable
    public isMeetingsLoading: boolean = true;
    @mobx.observable
    public selectedArtifactTypes: Set<ArtifactsTypeForBatchRequest> = new Set();
    @mobx.observable
    public selectedTab: TRequestArtifactsTab;
    @mobx.observable
    public artifactsMeetingsList: IRequestMeetingArtifactDTO[] = [];
    @mobx.observable
    public replaysMeetingsList: IRequestMeetingArtifactDTO[] = [];
    @mobx.observable
    public artifactsSelectedMeetingIds: Set<number> = new Set();
    @mobx.observable
    public replaysSelectedMeetingIds: Set<number> = new Set();
    @mobx.observable
    public isKnovioConnectionExist: boolean = false;
    @mobx.observable
    public isArtifactWebinarConnectionExist: boolean = false;
    @mobx.observable
    public isReplayWebinarConnectionExist: boolean = false;
    @mobx.observable
    public resolutionType: ResolutionMp4 = ResolutionMp4.DEFAULT;
    @mobx.observable
    public artifactRecordingType: RecordingType = RecordingType.DEFAULT;
    @mobx.observable
    public replayRecordingType: RecordingType = RecordingType.DEFAULT;
    @mobx.observable
    public reviewRequired: boolean = false;
    @mobx.observable
    public placeholderText: string = this.intl.formatMessage({
        id: 'oe.artifacts.replayIsNotAvailable',
        description: 'Replays placeholder text',
        defaultMessage: 'This replay is not yet available.'
    });
    constructor(
        public readonly eventSettings: IEventSettings,
        public readonly intl: IntlShape,
        private readonly requestMeetingsArtifactsInBatch: (payload: IRequestArtifactsPayload) => Promise<void>,
        @IoC.Inject private readonly meetingsDAO: IMeetingsDAO = IoC.Container.get(IMeetingsDAO),
    ) {

    }

    public get shouldDisableRequest(): boolean {
        return this.selectedMeetingsList === 0
            || !this.maxQuantityMeetingsValid
            || this.isMeetingsLoading
            || (this.reviewRequired && !!this.getPlaceholderTextLengthError())
            ;
    }
    public get maxQuantityMeetingsValid(): boolean { return this.selectedMeetingsList <= MAX_QUANTITY_OF_SELECTED_MEETINGS; }
    public get isValidArtifactsTabSelection(): boolean {
        return !!this.artifactsSelectedMeetingIds.size && !!this.selectedArtifactTypes.size;
    }
    public get selectedMeetingsList(): number { return this.selectedMeetingsIds(); }
    public get meetingListEmpty(): boolean {
        return !this.isMeetingsLoading && !this[`${this.selectedTab}MeetingsList`].length;
    }
    public get isAllMeetingsChecked(): boolean {
        return this[`${this.selectedTab}MeetingsList`].length
            && this[`${this.selectedTab}SelectedMeetingIds`].size === this[`${this.selectedTab}MeetingsList`].length;
    }

    private static handleApiException = (e: Error, title: string = 'Error'): void => {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    };

    @mobx.action
    public handleRequestMeetingsArtifactsInBatch = (): void => {
        if (this.shouldDisableRequest) return;
        const artifactsLength = this.isValidArtifactsTabSelection;
        const replaysLength = this.replaysSelectedMeetingIds.size;
        const payload: IRequestArtifactsPayload = {
            ...(artifactsLength && {
                artifacts: {
                    meetingIds: [...this.artifactsSelectedMeetingIds],
                    artifactTypes: [...this.selectedArtifactTypes],
                    resolution: this.resolutionType === ResolutionMp4.DEFAULT ? null : this.resolutionType,
                    recordingType: this.artifactRecordingType === RecordingType.DEFAULT ? null : this.artifactRecordingType,
                }
            }),
            ...(replaysLength && {
                replays: {
                    meetingIds: [...this.replaysSelectedMeetingIds],
                    reviewRequired: this.reviewRequired,
                    ...(this.isReplayWebinarConnectionExist && {
                        recordingType: this.replayRecordingType === RecordingType.DEFAULT ? null : this.replayRecordingType
                    }),
                    // Since CKEditor works with classes, but data is stored with inline styles,
                    // all CKEditor values need to be converted to the appropriate format
                    ...(this.reviewRequired && { placeholderText: transpileClassesToInlineStyles(this.placeholderText)})
                }
            })
        };

        this.requestMeetingsArtifactsInBatch(payload);
    };

    @mobx.action
    public getRequestArtifactsMeetings = async (selectedTab: TRequestArtifactsTab): Promise<void> => {
        if (this.selectedTab === selectedTab) return;
        try {
            mobx.runInAction(() => {
                this.isMeetingsLoading = true;
                this.selectedTab = selectedTab;
            });
            const result = await this.getMeetingByTabType()[selectedTab]();
            if (result) {
                this.onFetchMeetingsSuccess(result, selectedTab);
            }
        } catch (error) {
            RequestArtifactsPopoverViewModel.handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.isMeetingsLoading = false;
            });
        }
    };

    private getMeetingByTabType = (): Record<TRequestArtifactsTab, () => Promise<IEventMeetingsDTO | void>> => ({
        artifacts: async () => this.getEventArtifactsSources(),
        replays: async () => this.getEventReplaysSources()
    });

    private getEventArtifactsSources = async (): Promise<IEventMeetingsDTO | void> => {
        if (this.isMeetingsAlreadyFetched[this.selectedTab]) return;
        const params: TGetArtifactsSourcesRequestParams = {
            eventId: this.eventSettings.eventId,
            accountId: this.eventSettings.accountId,
            type: 'ARTIFACTS',
            sortFields: [
                {
                    order: SortOrder.Asc,
                    field: 'startDateTime'
                }
            ],
        };

        return await this.meetingsDAO.getEventArtifactsSources(params);
    };

    private getEventReplaysSources = async (): Promise<IEventMeetingsDTO | void> => {
        if (this.isMeetingsAlreadyFetched[this.selectedTab]) return;
        const params: TGetArtifactsSourcesRequestParams = {
            eventId: this.eventSettings.eventId,
            accountId: this.eventSettings.accountId,
            type: 'REPLAYS',
            sortFields: [
                {
                    order: SortOrder.Asc,
                    field: 'startDateTime'
                }
            ],
        };

        return await this.meetingsDAO.getEventArtifactsSources(params);
    };

    @mobx.action
    private onFetchMeetingsSuccess = (eventMeetingsDTO: IEventMeetingsDTO, selectedTab: TRequestArtifactsTab): void => {
        this[`${selectedTab}MeetingsList`] = eventMeetingsDTO.content.map(meeting => ({
            ...meeting,
            resolution: null,
            recordingType: null,
        }));
        this.isMeetingsAlreadyFetched[selectedTab] = true;
    };

    @mobx.action
    public toggleSelectedArtifactTypes = (type: ArtifactsTypeForBatchRequest): void => {
        if (this.selectedArtifactTypes.has(type)) {
            this.selectedArtifactTypes.delete(type);
            if (type === ArtifactsTypeForBatchRequest.MP4) {
                this.clearAllResolutionMeetings();
                this.clearAllRecordingTypesMeetings();
            }
        } else {
            this.selectedArtifactTypes.add(type);
            if (type === ArtifactsTypeForBatchRequest.MP4) {
                this.setResolutionSelectedMeetings();
                this.setRecordingTypeSelectedMeetings();
            }
        }
    };

    @mobx.action
    public toggleSingleMeeting = (meetingId: number): void => {
        if (this[`${this.selectedTab}SelectedMeetingIds`].has(meetingId)) {
            this[`${this.selectedTab}SelectedMeetingIds`].delete(meetingId);
            this.clearResolutionByMeetingId(meetingId);
            this.clearRecordingTypeByMeetingId(meetingId);
        } else {
            this[`${this.selectedTab}SelectedMeetingIds`].add(meetingId);
            this.setResolutionByMeetingId(meetingId);
            this.setRecordingTypeByMeetingId(meetingId);
        }
    };

    @mobx.action
    public toggleAllMeetings = (): void => {
        if (this.isAllMeetingsChecked) {
            this[`${this.selectedTab}SelectedMeetingIds`].clear();
            this.clearAllResolutionMeetings();
            this.clearAllRecordingTypesMeetings();
        } else {
            this.selectAllMeetings();
            this.setResolutionSelectedMeetings();
        }
    };

    @mobx.action
    private selectAllMeetings = (): void => {
        this[`${this.selectedTab}MeetingsList`].forEach(m => this[`${this.selectedTab}SelectedMeetingIds`].add(m.meetingId));
    };

    @mobx.action
    public setSelectedTab = (type: TRequestArtifactsTab): void => {
        this.getRequestArtifactsMeetings(type);
    };

    @mobx.action
    private selectedMeetingsIds = (): number => {
        return this.replaysSelectedMeetingIds.size + (this.selectedArtifactTypes.size
            ? this.artifactsSelectedMeetingIds.size : 0)
            ;
    };

    public handleArtifactMeetingCheckBox = (meetingId?: number): void => {
        if (meetingId) this.toggleSingleMeeting(meetingId);
        else this.toggleAllMeetings();

        this.checkKnovioConnectionInSelectedMeetings();
        this.checkWebinarConnectionInSelectedMeetings();
    };

    public handleArtifactTypeCheckBox = (type: ArtifactsTypeForBatchRequest): void => {
        this.toggleSelectedArtifactTypes(type);
        this.checkKnovioConnectionInSelectedMeetings();
        this.checkWebinarConnectionInSelectedMeetings();
    };

    @mobx.action
    public handleResolutionDropdown = (type: ResolutionMp4): void => {
        this.setResolutionType(type);
        this.setResolutionSelectedMeetings();
    };

    public handleRecordingTypeDropdown = (type: RecordingType): void => {
        this.setRecordingType(type);
        this.setRecordingTypeSelectedMeetings();
    };

    @mobx.action
    public checkKnovioConnectionInSelectedMeetings = (): void => {
        if (!this.checkMP4Selected()) return;
        const selectedIds = [...this[`${this.selectedTab}SelectedMeetingIds`]];
        let isKnovioConnectionExist = false;

        this[`${this.selectedTab}MeetingsList`].forEach(m => {
            if (isKnovioConnectionExist) return;
            selectedIds.find(id => {
                if (id === m.meetingId) isKnovioConnectionExist = this.getKnovioConnectionStatus(m);
            });
        });

        this.isKnovioConnectionExist = isKnovioConnectionExist;
    };

    @mobx.action
    public checkWebinarConnectionInSelectedMeetings = (): void => {
        if (this.selectedTab === RequestArtifactsTab.artifacts && !this.checkMP4Selected()) return;
        const selectedIds = [...this[`${this.selectedTab}SelectedMeetingIds`]];
        let isWebinarConnectionExist = false;

        this[`${this.selectedTab}MeetingsList`].forEach(m => {
            if (isWebinarConnectionExist) return;
            if (selectedIds.includes(m.meetingId)) {
                isWebinarConnectionExist = this.getWebinarConnectionStatus(m);
            }
        });

        if (this.selectedTab === RequestArtifactsTab.artifacts) {
            this.isArtifactWebinarConnectionExist = isWebinarConnectionExist;
        } else if (this.selectedTab === RequestArtifactsTab.replays) {
            this.isReplayWebinarConnectionExist = isWebinarConnectionExist;
        }
    };


    @mobx.action
    public setResolutionSelectedMeetings = (): void => {
        if (this.selectedTab === RequestArtifactsTab.artifacts && this.checkMP4Selected()) {
            const selectedIds = [...this[`${this.selectedTab}SelectedMeetingIds`]];
            this[`${this.selectedTab}MeetingsList`].forEach(m => {
                selectedIds.find(id => {
                    if (id === m.meetingId) this.setMeetingResolution(m, this.resolutionType);
                });
            });
        }
    };

    @mobx.action
    private setRecordingTypeSelectedMeetings = (): void => {
        const meetingsList = this.selectedTab === RequestArtifactsTab.artifacts ? this.artifactsMeetingsList : this.replaysMeetingsList;
        const selectedMeetingIds = this.selectedTab === RequestArtifactsTab.artifacts ? this.artifactsSelectedMeetingIds : this.replaysSelectedMeetingIds;
        const recordingType = this.selectedTab === RequestArtifactsTab.artifacts ? this.artifactRecordingType : this.replayRecordingType;

        meetingsList.forEach(m => {
            if (selectedMeetingIds.has(m.meetingId)) {
                this.setMeetingRecordingType(m, recordingType);
            }
        });
    };

    @mobx.action
    public clearAllResolutionMeetings = (): void => {
        if (this.selectedTab === RequestArtifactsTab.artifacts) {
            this[`${this.selectedTab}MeetingsList`].forEach(m => this.setMeetingResolution(m, null));
        }
    };

    @mobx.action
    private clearAllRecordingTypesMeetings = (): void => {
        if (this.selectedTab === RequestArtifactsTab.artifacts) {
            this.artifactsMeetingsList.forEach(m => this.setMeetingRecordingType(m, null));
        }
    };

    @mobx.action
    public setResolutionByMeetingId = (meetingId: number): void => {
        if (this.selectedTab === RequestArtifactsTab.artifacts) {
            if (!this.checkMP4Selected()) return;

            const meeting = this.findMeetingById(meetingId);
            this.setMeetingResolution(meeting, this.resolutionType);
        }
    };

    @mobx.action
    public setRecordingTypeByMeetingId = (meetingId: number): void => {
        if (this.selectedTab === RequestArtifactsTab.artifacts) {
            if (!this.checkMP4Selected()) return;

            const meeting = this.findMeetingById(meetingId);
            this.setMeetingRecordingType(meeting, this.artifactRecordingType);
        }
    };


    @mobx.action
    public clearResolutionByMeetingId = (meetingId: number): void => {
        if (this.selectedTab === RequestArtifactsTab.artifacts) {
            const meeting = this.findMeetingById(meetingId);
            this.setMeetingResolution(meeting, null);
        }
    };

    @mobx.action
    private clearRecordingTypeByMeetingId = (meetingId: number): void => {
        if (this.selectedTab === RequestArtifactsTab.artifacts) {
            const meeting = this.findMeetingById(meetingId);
            this.setMeetingRecordingType(meeting, null);
        }
    };

    @mobx.action
    public setMeetingResolution = (meeting: IRequestMeetingArtifactDTO, resolutionType: ResolutionMp4): void => {
        if (this.getKnovioConnectionStatus(meeting)) meeting.resolution = resolutionType;
    };

    @mobx.action
    private setMeetingRecordingType = (meeting: IRequestMeetingArtifactDTO, recordingType: RecordingType): void => {
        if (this.getWebinarConnectionStatus(meeting)) meeting.recordingType = recordingType;
    };

    @mobx.action
    public setResolutionType = (type: ResolutionMp4): void => {
        this.resolutionType = type;
    };

    @mobx.action
    public setRecordingType = (type: RecordingType): void => {
        this.selectedTab === RequestArtifactsTab.replays ? this.replayRecordingType = type : this.artifactRecordingType = type;
    };

    public checkMP4Selected = (): boolean => {
        return this.selectedArtifactTypes.has(ArtifactsTypeForBatchRequest.MP4);
    };

    public getKnovioConnectionStatus = (meeting: IRequestMeetingArtifactDTO): boolean => {
        return meeting.connections.some(c => c.type === ConnectionType.Knovio);
    };

    private getWebinarConnectionStatus = (meeting: IRequestMeetingArtifactDTO): boolean => {
        return this.selectedTab === RequestArtifactsTab.replays
            ? meeting.connections.some(c => c.type === ConnectionType.Webinar)
            : !meeting.connections.some(c => c.type === ConnectionType.Knovio) && meeting.connections.some(c => c.type === ConnectionType.Webinar);
    };

    public findMeetingById = (meetingId: number): IRequestMeetingArtifactDTO => {
        return this[`${this.selectedTab}MeetingsList`].find(m => m.meetingId === meetingId);
    };

    @mobx.action
    public setReviewRequired = (reviewRequired: boolean): void => {
        this.reviewRequired = reviewRequired;
    };

    @mobx.action
    public setPlaceholderText = (placeholderText: string): void => {
        this.placeholderText = placeholderText;
    };

    public getPlaceholderTextLengthError = (): null | string => {
        return this.placeholderText
            ? getShortRichTextLengthError(
                this.intl.formatMessage(
                    {
                        id: 'oe.artifacts.placeholderText',
                        defaultMessage: 'Placeholder Text',
                        description: 'Label text',
                    }),
                this.placeholderText,
            ) : null;
    };
};

export default RequestArtifactsPopoverViewModel;
